import { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

interface CapWarningBannerProps {
  hitCap: boolean;
  hardCap: number;
  windowDays: number;
  /** Number of runs actually collected (post-dedup). */
  collected: number;
}

export function CapWarningBanner({
  hitCap,
  hardCap,
  windowDays,
  collected,
}: CapWarningBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissal when the window changes — a different window = different dataset.
  useEffect(() => {
    setDismissed(false);
  }, [windowDays, hardCap]);

  if (!hitCap || dismissed) return null;

  // Two distinct truncation modes — the message and numbers differ so the user
  // isn't misled by a "truncated at 10,000" alert when the dataset is ~956.
  const reachedAppCap = collected >= hardCap;

  const title = reachedAppCap
    ? `Dataset truncated at ${hardCap.toLocaleString()} runs`
    : `Some runs may be missing (GitHub API limit reached)`;

  const description = reachedAppCap
    ? `This repo has more runs than the ${windowDays}-day window cap. Older runs were dropped — metrics reflect the most recent ${hardCap.toLocaleString()} runs only.`
    : `GitHub caps each window query at ~1,000 results. Weekly slicing couldn't split the window finely enough. Collected ${collected.toLocaleString()} runs — some from this window may be missing. Try a shorter window (e.g. ${Math.max(1, Math.floor(windowDays / 2))} days) for complete data.`;

  return (
    <Alert>
      <AlertTriangle className="size-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
        <span>{description}</span>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setDismissed(true)}
          className="ml-auto"
          aria-label="Dismiss warning"
        >
          <X className="size-4" />
        </Button>
      </AlertDescription>
    </Alert>
  );
}
